/**
 * Total invocation of caller-supplied callbacks (WI-TV-W5-004).
 *
 * Every callback this SPA hands out — telemetry sinks, submission observers,
 * completion notifications — is typed as returning `void`, but TypeScript still
 * accepts an `async` function there. Such a callback fails by returning a
 * rejected promise rather than by throwing, which no `try/catch` around the call
 * can see, and the rejection then surfaces as an unhandled rejection.
 *
 * These helpers make the boundary total for both failure modes without ever
 * awaiting the callback, so reporting can neither delay nor influence the
 * outcome that has already been determined.
 */

/** A callback that may fail synchronously or by rejecting. */
export type MaybePromise<T> = T | PromiseLike<T>;

/**
 * Attaches rejection handling to whatever a callback returned. The thenable is
 * detected defensively because reading `.then`, or calling it, can itself throw
 * on a hostile value; the value is never awaited.
 */
export function absorbRejection(value: unknown): void {
  if (value === null || (typeof value !== "object" && typeof value !== "function")) return;

  let then: unknown;
  try {
    then = (value as { then?: unknown }).then;
  } catch {
    // A throwing `then` getter is not a promise worth following.
    return;
  }
  if (typeof then !== "function") return;

  const noop = (): void => {};
  try {
    // Invoked once, with the rejection handler in place from the outset, so a
    // rejection can never escape as an unhandled rejection.
    (then as (onFulfilled: () => void, onRejected: () => void) => unknown).call(value, noop, noop);
  } catch {
    // A `then` that throws when invoked is contained like any other failure.
  }
}

/**
 * Invokes `callback` exactly once and contains every way it can fail. Nothing is
 * logged: the failure may carry arbitrary detail and must never reach a console.
 */
export function contain(callback: () => unknown): void {
  try {
    absorbRejection(callback());
  } catch {
    // Best-effort by construction; the caller's outcome already stands.
  }
}
